const isPlainObject = (value) => value && typeof value === "object" && !Array.isArray(value);

// Formas de detectar el robo (modo_hurto). Las dos ultimas agregan una condicion antes de guardar: alertan
// menos que "guarda", nunca mas.
export const SHOPLIFTING_MODES = [
  { value: "guarda", label: "Solo guarda", help: "Alerta cuando la persona guarda el producto. Es la forma de siempre." },
  { value: "agarra_y_guarda", label: "Agarra y despues guarda", help: "Ademas tiene que haber agarrado el producto justo antes de guardarlo. Menos falsas alarmas; puede perder robos donde no se vio el agarre." },
  { value: "agarra_mira_y_guarda", label: "Agarra, mira y guarda", help: "Antes de guardar tiene que haber agarrado y mirado el producto, en cualquier orden. Es la mas exigente." },
];

export const shopliftingModeLabel = (value) => SHOPLIFTING_MODES.find((mode) => mode.value === value)?.label;

// Reglas del hurto que el motor acepta por camara (detector_params.shoplifting), con los mismos rangos que
// valida al guardar (core/reglas_hurto.py). Lo que la camara no define lo toma de detections.json.
// requiresMode: solo se muestran si el motor conectado ya tiene modo_hurto en detections.json.
const RULES_WITH_PREVIOUS_STEP = "Solo cuenta con \"Agarra y despues guarda\" o \"Agarra, mira y guarda\".";
export const SHOPLIFTING_RULES = [
  { key: "modo_hurto", label: "Forma de detectar el robo", type: "choice", options: SHOPLIFTING_MODES, requiresMode: true },
  { key: "pre_susp_min_frames", label: "Cuadros seguidos agarrando (o mirando) antes de guardar", type: "int", min: 1, max: 49, requiresMode: true, help: RULES_WITH_PREVIOUS_STEP },
  { key: "max_gap_before_put", label: "Cuadros permitidos entre agarrar y guardar", type: "int", min: 0, max: 49, requiresMode: true, help: RULES_WITH_PREVIOUS_STEP },
  { key: "putitem_conf_thr", label: "Confianza para contar el gesto de guardar", type: "float", min: 0, max: 1, help: "Mas alto = menos falsas alarmas, pero puede perder robos reales." },
  { key: "putitem_min_frames", label: "Cuadros seguidos para alertar", type: "int", min: 1, max: 50 },
  { key: "fsm_window", label: "Ventana de analisis (cuadros)", type: "int", min: 1, max: 50, help: "No puede ser menor que los cuadros seguidos para alertar." },
  { key: "yowo_conf", label: "Filtro del modelo (confianza minima)", type: "float", min: 0, max: 1, help: "No puede ser mayor que la confianza para contar el gesto." },
  { key: "yowo_iou", label: "Superposicion de cajas (IoU)", type: "float", min: 0, max: 1 },
  { key: "alert_cooldown_sec", label: "Pausa entre alertas de la misma persona (s)", type: "float", min: 0, max: null },
  { key: "dedupe_window_sec", label: "Anti-duplicados (s)", type: "float", min: 0, max: 3600 },
  { key: "clip_pre_sec", label: "Video antes de la alerta (s)", type: "float", min: 0, max: 5 },
  { key: "clip_post_sec", label: "Video despues de la alerta (s)", type: "float", min: 1, max: 30 },
  { key: "use_person_gate", label: "Exigir persona detectada", type: "bool" },
  { key: "hold_ms", label: "Mantener la accion (ms)", type: "int", min: 0, max: null },
  { key: "motion_threshold", label: "Umbral de movimiento", type: "int", min: 0, max: null },
  { key: "no_person_threshold", label: "Cuadros sin persona para vaciar el buffer", type: "int", min: 1, max: null },
  { key: "track_every_n", label: "Seguimiento cada N cuadros", type: "int", min: 1, max: null },
  { key: "track_sync_tolerance", label: "Tolerancia de atraso del seguimiento (cuadros)", type: "int", min: 0, max: null },
];

export const ownShopliftingRules = (camera) => {
  const rules = camera?.detector_params?.shoplifting;
  return isPlainObject(rules) ? rules : {};
};

// Las reglas de modo_hurto se ocultan con un motor que no lo conoce (no harian nada), salvo que la camara ya
// las tenga: asi se pueden quitar.
export const visibleShopliftingRules = (camera, globalRules) => {
  const own = ownShopliftingRules(camera);
  const engineHasMode = isPlainObject(globalRules) && "modo_hurto" in globalRules;
  return SHOPLIFTING_RULES.filter((rule) => !rule.requiresMode || engineHasMode || rule.key in own);
};

export const shopliftingRuleErrors = (camera, globalRules) => {
  const own = ownShopliftingRules(camera);
  if (!Object.keys(own).length) return [];
  const errors = [];
  SHOPLIFTING_RULES.forEach(({ key, label, type, min, max, options }) => {
    if (!(key in own)) return;
    const value = own[key];
    if (type === "bool") {
      if (typeof value !== "boolean") errors.push(`${label}: tiene que ser si o no`);
      return;
    }
    if (type === "choice") {
      if (!options.some((option) => option.value === value)) {
        errors.push(`${label}: tiene que ser una de: ${options.map((option) => option.label).join(", ")}`);
      }
      return;
    }
    const valid = typeof value === "number" && Number.isFinite(value)
      && (type !== "int" || Number.isInteger(value))
      && value >= min && (max === null || value <= max);
    if (!valid) {
      const kind = type === "int" ? "un entero" : "un numero";
      errors.push(`${label}: tiene que ser ${kind} ${max === null ? `mayor o igual a ${min}` : `entre ${min} y ${max}`}`);
    }
  });
  const effective = { ...(isPlainObject(globalRules) ? globalRules : {}), ...own };
  if (effective.fsm_window < effective.putitem_min_frames) {
    errors.push(`La ventana de analisis (${effective.fsm_window}) no puede ser menor que los cuadros seguidos para alertar (${effective.putitem_min_frames}): nunca alertaria`);
  }
  if (effective.yowo_conf > effective.putitem_conf_thr) {
    errors.push(`El filtro del modelo (${effective.yowo_conf}) no puede ser mayor que la confianza para contar el gesto (${effective.putitem_conf_thr})`);
  }
  const previous = effective.pre_susp_min_frames ?? 5;
  if ((effective.modo_hurto ?? "guarda") !== "guarda" && previous + effective.putitem_min_frames > effective.fsm_window) {
    errors.push(`Los cuadros agarrando antes de guardar (${previous}) mas los cuadros seguidos para alertar (${effective.putitem_min_frames}) no pueden pasar de la ventana de analisis (${effective.fsm_window}): nunca alertaria`);
  }
  return errors;
};

export const formatRuleValue = (value) => {
  if (value === undefined || value === null) return "sin definir";
  if (typeof value === "boolean") return value ? "si" : "no";
  return shopliftingModeLabel(value) || String(value);
};
